import { useState } from "react";
import { FiUploadCloud } from "react-icons/fi";
import { useUserStore } from "../../stores";
import { useUpdateRider } from "../../hooks/rider-operations/useRiderMutations";
import { useUpdateDriver } from "../../hooks/driver-operations/useDriverMutations";
import { showToast } from "../../hooks/useToast";

const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

const validFileTypes = ["image/jpeg", "image/png"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

function UploadProfilePicture({ onSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");

  const { userId, userType } = useUserStore();
  const { updateRider } = useUpdateRider();
  const { updateDriver } = useUpdateDriver();

  const handleFileSelect = (e) => {
    setError("");
    const file = e.target.files[0];
    if (!file) return;

    if (!validFileTypes.includes(file.type)) {
      setError("File must be JPG or PNG format.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("File must be under 5MB.");
      return;
    }

    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError("");

    try {
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

      const response = await fetch(CLOUDINARY_UPLOAD_URL, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      const imageUrl = data.secure_url;

      // Save URL to database via GraphQL mutation
      const updateFn = userType === "driver" ? updateDriver : updateRider;
      await updateFn({
        variables: {
          id: userId,
          profilePicture: imageUrl,
        },
      });

      showToast.success("Profile picture updated!");
      onSuccess?.();
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload image. Please try again.");
      showToast.error("Failed to upload profile picture.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center text-center">
      <h3 className="text-white text-xl font-bold mb-4">
        Upload Profile Picture
      </h3>

      {preview ? (
        // Preview selected image
        <div className="mb-4">
          <img
            src={preview}
            alt="Preview"
            className="w-32 h-32 object-cover rounded-full border-2 border-white/30"
          />
        </div>
      ) : (
        // File picker dropzone
        <label
          htmlFor="profile-picture-input"
          className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-white/30 rounded-lg cursor-pointer hover:border-white/50 hover:bg-white/5 transition-colors mb-4"
        >
          <FiUploadCloud className="text-white/60 mb-2" size={32} />
          <p className="text-white/80 text-sm font-medium">
            Click to select an image
          </p>
          <p className="text-white/50 text-xs mt-1">
            JPG or PNG, max 5MB
          </p>
          <input
            id="profile-picture-input"
            type="file"
            accept=".jpg,.jpeg,.png"
            className="hidden"
            onChange={handleFileSelect}
          />
        </label>
      )}

      {error && (
        <p className="text-main-red-light text-sm mb-3">{error}</p>
      )}

      {preview && (
        <button
          type="button"
          onClick={handleUpload}
          disabled={uploading}
          className="btn btn-green btn-md w-full"
        >
          {uploading ? "Uploading..." : "Save"}
        </button>
      )}
    </div>
  );
}

export default UploadProfilePicture;
